"use client";

import { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';
import Link from 'next/link';

export default function BestProducts() {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const autoScrollIntervalRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    async function fetchProducts() {
      try {
        const res = await fetch('/api/products?featured=true&limit=20&sortBy=rating&sortOrder=desc');
        if (res.ok) {
          const data = await res.json();
          if (data.success) {

            const transformed = data.data.map(product => {
              const images = product.images ? JSON.parse(product.images) : [];
              return {
                id: product.id,
                name: product.name,
                slug: product.slug,
                price: product.price,
                originalPrice: product.originalPrice,
                image: images[0] || null,
                badge: product.discount ? `-${product.discount}%` : null,
                badgeColor: 'green',
              };
            });
            setProducts(transformed);
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);


  const fallbackProducts = [
    {
      id: 1,
      name: 'iPhone 14',
      price: 549,
      originalPrice: 899,
      image: 'https://d2e6ccujb3mkqf.cloudfront.net/400b5725-cbcb-4b97-a7b8-0a2c1d4098ef-1_ea517b48-9556-4e8a-9cdf-38072d2682df.jpg',
    },
    {
      id: 2,
      name: 'iPhone 15',
      price: 649,
      originalPrice: 999,
      image: 'https://d2e6ccujb3mkqf.cloudfront.net/8debb43b-94ef-4ec4-8589-9520e9eaa983-1_788be5ce-0df1-4871-96b2-50271a0fdb40.jpg',
    },
    {
      id: 3,
      name: 'iPhone 12',
      price: 399,
      originalPrice: 799,
      image: 'https://d2e6ccujb3mkqf.cloudfront.net/fc516a2d-d3bc-4ed6-99d8-a4f51e550d4d-1_557d2545-1bb4-440e-afb7-d7b2f9b84e01.jpg',
    },
    {
      id: 4,
      name: 'iPhone 11',
      price: 329,
      originalPrice: 699,
      image: 'https://d2e6ccujb3mkqf.cloudfront.net/0106bb6f-3975-4794-b9a0-7d920669484c-1_6699e84c-942e-4ba2-8dd9-5a70fbed78fe.jpg',
    },
    {
      id: 5,
      name: 'iPhone 16',
      price: 799,
      originalPrice: 1099,
      image: 'https://d2e6ccujb3mkqf.cloudfront.net/7f5bdd13-8f4b-49ee-a94a-8b578deb328d-1_01ccd070-bed9-4da0-af93-4e020757bb6c.jpg',
    },
  ];

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    const scrollAmount = 320;
    if (container) {
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const startAutoScroll = () => {
    if (autoScrollIntervalRef.current) clearInterval(autoScrollIntervalRef.current);
    setIsAutoScrolling(true);

    autoScrollIntervalRef.current = setInterval(() => {
      const container = scrollContainerRef.current;
      if (container) {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const maxScroll = scrollWidth - clientWidth;


        if (scrollLeft >= maxScroll - 10) {
          container.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          container.scrollBy({
            left: 320,
            behavior: 'smooth',
          });
        }
      }
    }, 5000);
  };

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
    }
    setIsAutoScrolling(false);
  };

  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', checkScroll);
    checkScroll();

    startAutoScroll();

    return () => {
      container?.removeEventListener('scroll', checkScroll);
      stopAutoScroll();
    };
  }, []);

  return (
    <section id="best-products" className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#23994F]/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-400/5 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        <div className="mb-8 sm:mb-12 lg:mb-14 fade-in text-center">
          <div className="inline-flex items-center gap-2 bg-[#23994F]/10 px-4 py-2 rounded-full mb-4">
            <span className="text-sm font-semibold text-[#23994F]">Sélection premium</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Nos meilleurs produits
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez notre sélection exclusive de produits les plus populaires avec garantie 12 mois
          </p>
        </div>


        <div
          className="relative fade-in"
          style={{ animationDelay: '0.2s' }}
          onMouseEnter={stopAutoScroll}
          onMouseLeave={startAutoScroll}
        >

          <button
            onClick={() => {
              scroll('left');
              stopAutoScroll();
              setTimeout(() => startAutoScroll(), 5000);
            }}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 sm:p-4 bg-white/95 backdrop-blur-sm rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 -ml-4 sm:-ml-6 hidden lg:flex items-center justify-center group border-2 border-gray-100 ${
              !canScrollLeft ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#23994F] hover:border-[#23994F]'
            }`}
            disabled={!canScrollLeft}
          >
            <ChevronLeft size={24} className={`transition-colors ${!canScrollLeft ? 'text-gray-400' : 'text-[#23994F] group-hover:text-white'}`} />
          </button>

          <button
            onClick={() => {
              scroll('right');
              stopAutoScroll();
              setTimeout(() => startAutoScroll(), 5000);
            }}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 sm:p-4 bg-white/95 backdrop-blur-sm rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 -mr-4 sm:-mr-6 hidden lg:flex items-center justify-center group border-2 border-gray-100 ${
              !canScrollRight ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#23994F] hover:border-[#23994F]'
            }`}
            disabled={!canScrollRight}
          >
            <ChevronRight size={24} className={`transition-colors ${!canScrollRight ? 'text-gray-400' : 'text-[#23994F] group-hover:text-white'}`} />
          </button>


          <div
            ref={scrollContainerRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto lg:overflow-x-hidden scroll-smooth scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0"
            style={{ scrollBehavior: 'smooth', touchAction: 'pan-x' }}
          >
            {loading ? (
              <div className="text-center py-12 w-full">
                <p className="text-gray-600">Chargement des produits...</p>
              </div>
            ) : products.length > 0 ? (
              products.map((product, idx) => (
                <div key={product.id} className="stagger-item shrink-0 w-72 sm:w-80" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <ProductCard {...product} priority={idx < 3} />
                </div>
              ))
            ) : (
              <div className="text-center py-12 w-full">
                <p className="text-gray-600">Aucun produit disponible</p>
              </div>
            )}
          </div>
        </div>


        <div className="text-center mt-8 sm:mt-12 fade-in" style={{ animationDelay: '0.4s' }}>
          <Link href="/products" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 min-h-12 bg-gradient-to-r from-[#23994F] to-[#1a7840] text-white font-bold text-sm sm:text-base rounded-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] group">
            <span>Voir tous les produits</span>
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}
